import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase-config';
import { 
  SearchIcon, 
  FilterIcon
} from '@heroicons/react/outline';
import RFQCard from './RFQCard';

const PublicRFQFeed = () => {
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([{ id: 'all', name: 'All Categories' }]);
  const navigate = useNavigate();

  // Fetch RFQs from Firebase
  const fetchRFQs = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Use the correct "posts" collection and query structure
      let q = query(
        collection(db, 'posts'),
        where('approved', '==', true),
        orderBy('timestamp', 'desc'),
        limit(20)
      );
      
      const querySnapshot = await getDocs(q);
      const rfqData = [];
      const categorySet = new Set();
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Post Data:', data); // Debug log
        
        // Extract category from the data
        const category = data.categories && data.categories.length > 0 
          ? data.categories[0] 
          : data.channel || 'General';
        
        // Add category to set for unique categories
        if (category && category !== 'General') {
          categorySet.add(category);
        }
        
        // Map the fields correctly based on twerceapp structure
        rfqData.push({
          id: doc.id,
          title: data.textContent ? data.textContent.substring(0, 100) + (data.textContent.length > 100 ? '...' : '') : 'Untitled RFQ',
          description: data.textContent || 'No description available',
          category: category,
          budget: data.budget || 0,
          location: data.location ? (typeof data.location === 'object' ? data.location.name : data.location) : 'Location not specified',
          company: data.authorName || 'Company not specified',
          postedDate: data.timestamp ? new Date(data.timestamp).toISOString() : new Date().toISOString(),
          urgency: 'medium', // Default since not stored in posts
          views: data.replyCount || 0,
          replyCount: data.replyCount || 0,
          userId: data.author,
          userName: data.authorName,
          media: data.media || []
        });
      });
      
      // Sort by date if available
      rfqData.sort((a, b) => new Date(b.postedDate) - new Date(a.postedDate));
      
      setRfqs(rfqData);
      console.log('Fetched RFQs:', rfqData.length); // Debug log
      
      // Generate categories from the data
      const dynamicCategories = [
        { id: 'all', name: 'All Categories' },
        ...Array.from(categorySet).sort().map(cat => ({
          id: cat,
          name: cat,
          count: rfqData.filter(rfq => rfq.category === cat).length
        }))
      ];
      
      // Add 'General' category if it exists in the data
      if (rfqData.some(rfq => rfq.category === 'General')) {
        dynamicCategories.push({ 
          id: 'General', 
          name: 'General',
          count: rfqData.filter(rfq => rfq.category === 'General').length
        });
      }
      
      setCategories(dynamicCategories);
      console.log('Dynamic categories:', dynamicCategories); // Debug log
      
      // Show message if no RFQs found
      if (rfqData.length === 0) {
        setError('No RFQs found. This could be because: 1) No approved posts exist yet, 2) There\'s a connection issue, or 3) The database is empty.');
      }
      
    } catch (err) {
      console.error('Error fetching RFQs:', err);
      setError(`Failed to load RFQs: ${err.message}. This might be due to Firebase configuration or network issues.`);
      
      // Don't set fallback data - let the user see the error and take action
      setRfqs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRFQs();
  }, []);

  const handlePostRFQ = () => {
    // Navigate directly to create RFQ form (now public)
    navigate('/create-rfq');
  };

  const handleSubmitQuote = () => {
    // Market feed is public, no authentication needed
    navigate('/market-feed');
  };

  // Filter RFQs based on search and category
  const filteredRFQs = rfqs.filter(rfq => {
    const matchesSearch = searchTerm === '' || 
      rfq.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rfq.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rfq.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rfq.category.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || rfq.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 animate-pulse">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-start justify-between mb-3">
              <div className="h-5 bg-gray-200 rounded w-16"></div>
              <div className="h-4 bg-gray-200 rounded w-8"></div>
            </div>
            <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-full mb-1"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          </div>
          <div className="p-6 pt-4">
            <div className="flex gap-3">
              <div className="flex-1 h-10 bg-gray-200 rounded-lg"></div>
              <div className="flex-1 h-10 bg-gray-200 rounded-lg"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="py-16 bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="container mx-auto px-4 lg:px-8">
          {/* Enhanced Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 rounded-full text-sm font-medium mb-6 border border-blue-200">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></span>
              Live Opportunities
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 bg-gradient-to-r from-gray-900 via-blue-900 to-gray-900 bg-clip-text text-transparent">
              Real Business Opportunities Available Now
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Don't miss out on these active RFQs from verified businesses across Nigeria. Browse opportunities and submit your competitive quotes today.
            </p>
          </div>

          {/* Enhanced Error Message */}
          {error && (
            <div className="mb-8 p-6 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-2xl shadow-lg">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-yellow-800 mb-3">Unable to Load RFQs</h3>
                  <p className="text-yellow-700 text-sm mb-4">{error}</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={fetchRFQs}
                      className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-yellow-600 to-orange-600 text-white text-sm font-medium rounded-lg hover:from-yellow-700 hover:to-orange-700 transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Try Again
                    </button>
                    <button
                      onClick={handlePostRFQ}
                      className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-orange-600 to-red-600 text-white text-sm font-medium rounded-lg hover:from-orange-700 hover:to-red-700 transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Post Your RFQ
                    </button>
                    <button
                      onClick={handleSubmitQuote}
                      className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white text-sm font-medium rounded-lg hover:from-gray-700 hover:to-gray-800 transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Submit Quotes
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => setError('')}
                  className="ml-4 p-2 text-yellow-600 hover:text-yellow-800 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Enhanced Search and Filter */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-100">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <SearchIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search RFQs, companies, or keywords..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
              </div>

              {/* Category Filter */}
              <div className="relative">
                <FilterIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full lg:w-auto pl-10 pr-8 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white transition-all duration-200"
                >
                  {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.id === 'all' 
                      ? `${category.name} (${rfqs.length})`
                      : `${category.name} (${category.count || 0})`
                    }
                  </option>
                ))}
                </select>
              </div>
            </div>
          </div>

          <LoadingSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="py-16 bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Enhanced Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 rounded-full text-sm font-medium mb-6 border border-blue-200">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></span>
            Live Opportunities
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 bg-gradient-to-r from-gray-900 via-blue-900 to-gray-900 bg-clip-text text-transparent">
            Real Business Opportunities Available Now
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Don't miss out on these active RFQs from verified businesses across Nigeria. Browse opportunities and submit your competitive quotes today.
          </p>
        </div>

        {/* Enhanced Error Message */}
        {error && (
          <div className="mb-8 p-6 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-2xl shadow-lg">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-yellow-800 mb-3">Unable to Load RFQs</h3>
                <p className="text-yellow-700 text-sm mb-4">{error}</p>
                <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={fetchRFQs}
                    className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-yellow-600 to-orange-600 text-white text-sm font-medium rounded-lg hover:from-yellow-700 hover:to-orange-700 transition-all duration-200 shadow-md hover:shadow-lg"
                >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  Try Again
                </button>
                <button
                    onClick={handlePostRFQ}
                    className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-orange-600 to-red-600 text-white text-sm font-medium rounded-lg hover:from-orange-700 hover:to-red-700 transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Post Your RFQ
                  </button>
                  <button
                    onClick={handleSubmitQuote}
                    className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white text-sm font-medium rounded-lg hover:from-gray-700 hover:to-gray-800 transition-all duration-200 shadow-md hover:shadow-lg"
                >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Submit Quotes
                </button>
                </div>
              </div>
              <button
                onClick={() => setError('')}
                className="ml-4 p-2 text-yellow-600 hover:text-yellow-800 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Enhanced Search and Filter */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-100">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <SearchIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search RFQs, companies, or keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            {/* Category Filter */}
            <div className="relative">
              <FilterIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full lg:w-auto pl-10 pr-8 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white transition-all duration-200"
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.id === 'all' 
                      ? `${category.name} (${rfqs.length})`
                      : `${category.name} (${category.count || 0})`
                    }
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Enhanced RFQ Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredRFQs.map((rfq) => (
            <RFQCard key={rfq.id} rfq={rfq} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PublicRFQFeed; 